/**
 * InlineCrmObjectPanel — hosts the CRM record regions as a REAL CHILD of a
 * page's layout (a `SplitView` panel), driven by the same URL-synced slideout
 * stack the global portal renderer uses.
 *
 * Mount this inside a SplitView panel on a page that has claimed the
 * `entity` kind via `registerInlineHost(['entity'])` — the global
 * `SlideoutRenderer` stands down for claimed kinds, so exactly one surface
 * renders the record. Stack wiring mirrors `CrmObjectSlideout`: close clears
 * the stack, back pops it, and the unsaved-changes guard registers as the
 * stack push guard.
 *
 * There is no `SlideoutMenu` here, so this component IS the chrome: it
 * publishes `SurfaceDismissContext` and renders `SlideoutMenu.Header` (which
 * carries the close button) around the same header/body/footer regions the
 * portaled slideout uses.
 */

import { type ReactElement, useCallback, useEffect, useMemo } from 'react';

import { useOrg } from '@/contexts/OrgContext';
import { SlideoutMenu } from '@/lib/base/overlays/slideout-menu';
import {
  SurfaceDismissContext,
  type SurfaceDismissContextValue,
} from '@/lib/base/overlays/surface-dismiss-context';

import { SlideoutOriginProvider } from '../contexts/SlideoutOriginContext';
import {
  type SlideoutEntry,
  useSlideoutStackContext,
} from '../contexts/SlideoutStackContext';
import { useSlideoutStack } from '../hooks/useSlideoutStack';
import { RecordSurfaceProvider } from './contexts/RecordSurfaceContext';
import { useGuardedDismiss } from './hooks/useGuardedDismiss';
import { RECORD_HEADER_ALIGN, RECORD_HEADER_CLASS } from './record-layout';
import {
  RecordSlideoutBody,
  RecordSlideoutFooter,
  RecordSlideoutHeaderActions,
  RecordSlideoutHeaderContent,
  RecordSlideoutHeaderIdentity,
} from './record-slideout-regions';

// Static slot elements — the regions read everything from context.
const HEADER_SLOT = <RecordSlideoutHeaderContent />;
const HEADER_ACTIONS_SLOT = <RecordSlideoutHeaderActions />;
const HEADER_SECOND_ROW_SLOT = <RecordSlideoutHeaderIdentity />;

export interface InlineCrmObjectPanelProps {
  /** Stack entry this panel renders. Must be `kind: 'entity'`. */
  entry: SlideoutEntry;
}

export function InlineCrmObjectPanel({
  entry,
}: InlineCrmObjectPanelProps): ReactElement | null {
  const { currentOrg } = useOrg();
  const { stack, popSlideout, dismissToProposalBase } = useSlideoutStack();
  const { registerPushGuard, unregisterPushGuard } = useSlideoutStackContext();

  const registerGuard = useCallback(
    (guard: ((action: () => void) => boolean) | null) => {
      if (guard) {
        registerPushGuard(guard);
      } else {
        unregisterPushGuard();
      }
    },
    [registerPushGuard, unregisterPushGuard]
  );

  // Clean up the push guard when this panel unmounts (user closed it, the
  // host lost eligibility, or another kind was pushed on top).
  // `unregisterPushGuard` is a stable context callback, so it is a real
  // dependency rather than a latest-ref — the effect never re-runs in practice.
  useEffect(() => {
    return () => {
      unregisterPushGuard();
    };
  }, [unregisterPushGuard]);

  const { close, back, handleGuardChange } = useGuardedDismiss({
    onClose: dismissToProposalBase,
    onBack: popSlideout,
    onGuardChange: registerGuard,
  });

  const showBackButton = stack.length > 1;
  const dismiss = useMemo<SurfaceDismissContextValue>(
    () => ({
      close,
      back: showBackButton ? back : undefined,
      isEntering: false,
      isExiting: false,
    }),
    [close, back, showBackButton]
  );

  if (!currentOrg || entry.kind !== 'entity') {
    return null;
  }

  return (
    <RecordSurfaceProvider
      key={entry.id}
      objectId={entry.id}
      onGuardChange={handleGuardChange}
    >
      {/* Clicks inside this panel's content are "from within the stack" —
          useOpenSlideout resolves them to pushSlideout (drill-down layers),
          exactly as in the overlay CrmObjectSlideout. */}
      <SlideoutOriginProvider entry={entry}>
        <SurfaceDismissContext value={dismiss}>
          {/* `relative` anchors the floating SaveBar; the Content scroller
            below is not positioned, so the bar escapes its overflow. */}
          <div
            className='flex h-full w-full flex-col relative'
            data-component='inline-record-panel'
          >
            <SlideoutMenu.Header
              className={RECORD_HEADER_CLASS}
              align={RECORD_HEADER_ALIGN}
              actions={HEADER_ACTIONS_SLOT}
              secondRow={HEADER_SECOND_ROW_SLOT}
            >
              {HEADER_SLOT}
            </SlideoutMenu.Header>
            <SlideoutMenu.Content className='px-0 md:px-0'>
              <RecordSlideoutBody />
            </SlideoutMenu.Content>
            <RecordSlideoutFooter />
          </div>
        </SurfaceDismissContext>
      </SlideoutOriginProvider>
    </RecordSurfaceProvider>
  );
}
